import { SampleCategories } from '@arpeggi/shared'
import { ISongStore } from '../../stores/song/SongStore'
import { generateSong, safeUpdate, StemGenerator, StemGeneratorType } from '../GeneratorUtils'
import { RandProb, RandRange } from '../RandomUtils'
import { SongGenerator } from '../SongGenerator'
import { TrapDrumGenerator } from './TrapDrumGenerator'
import { TrapMelodicGenerator } from './TrapMelodicGenerator'

const TEMPO_MIN = 160
const TEMPO_MAX = 205

// Interface for song + arrangement generation.
export class TrapSongGenerator implements SongGenerator {
  // Sample list.
  melodicGenerator: TrapMelodicGenerator
  drumGenerator: TrapDrumGenerator

  constructor() {
    this.melodicGenerator = new TrapMelodicGenerator()
    this.drumGenerator = new TrapDrumGenerator()
  }
  getMelodicGenerator() {
    return this.melodicGenerator
  }
  getDrumGenerator() {
    return this.drumGenerator
  }
  generateFullSong(songStore: ISongStore) {
    generateSong(
      songStore,
      this.melodicGenerator,
      this.drumGenerator,
      this.generateTempo(),
      [TRAP_LEAD_STEM_TYPE, TRAP_HARMONY_STEM_TYPE, TRAP_BASS_STEM_TYPE],
      TrapSongGenerator.generateDrumArrangement(songStore.songDisplayLength || 16)
    )
  }

  // Get a random tempo.
  generateTempo(): number {
    return RandRange(TEMPO_MIN, TEMPO_MAX)
  }

  static generateLeadArrangement(length: number) {
    const arrangement = Array<number>(length).fill(1)
    if (RandProb(0.3)) {
      safeUpdate(arrangement, 6, 0)
      safeUpdate(arrangement, 7, 0)
    }
    if (RandProb(1)) {
      safeUpdate(arrangement, 8, 0)
      safeUpdate(arrangement, 9, 0)
    }
    return arrangement
  }

  static generateHarmonyArrangement(length: number) {
    const arrangement = Array<number>(length).fill(0)
    for (let i = 4; i < arrangement.length; i += 8) {
      safeUpdate(arrangement, i, 1)
      safeUpdate(arrangement, i + 1, 1)
      safeUpdate(arrangement, i + 2, 1)
      safeUpdate(arrangement, i + 3, 1)
    }
    return arrangement
  }

  static generateBassArrangement(length: number): number[] {
    const arrangement = Array<number>(length).fill(1)

    // Alternate 1 and 2!
    for (let i = 0; i < length; i++) {
      if (i % 4 === 0) {
        safeUpdate(arrangement, i, 2)
        safeUpdate(arrangement, i + 1, 2)
      }
    }

    // Mute intro
    if (RandProb(0.8)) {
      safeUpdate(arrangement, 0, 0)
      if (RandProb(0.6)) {
        safeUpdate(arrangement, 1, 0)
        if (RandProb(0.3)) {
          safeUpdate(arrangement, 2, 0)
          safeUpdate(arrangement, 3, 0)
        }
      }
    }

    // Drop out before final drop
    if (RandProb(0.3)) {
      safeUpdate(arrangement, 10, 0)
      safeUpdate(arrangement, 11, 0)
    }

    return arrangement
  }

  // TODO do this in a slightly smarter way
  static generateDrumArrangement(length: number) {
    const arrangement = Array<number>(length).fill(1)

    // Alternate 1 and 2!
    for (let i = 0; i < length; i++) {
      if (i % 4 === 0) {
        safeUpdate(arrangement, i, 2)
        safeUpdate(arrangement, i + 1, 2)
      }
    }

    // Mute intro
    if (RandProb(0.8)) {
      safeUpdate(arrangement, 0, 0)
      if (RandProb(0.6)) {
        safeUpdate(arrangement, 1, 0)
        if (RandProb(0.3)) {
          safeUpdate(arrangement, 2, 0)
          safeUpdate(arrangement, 3, 0)
        }
      }
    }
    return arrangement
  }
}

const TRAP_LEAD_STEM_TYPE: StemGenerator = {
  type: StemGeneratorType.LEAD,
  categories: SampleCategories.Synth,
  generateArrangement: TrapSongGenerator.generateLeadArrangement,
  defaultGain: 0.1
}
const TRAP_HARMONY_STEM_TYPE: StemGenerator = {
  type: StemGeneratorType.HARMONY,
  categories: SampleCategories.Synth,
  generateArrangement: TrapSongGenerator.generateHarmonyArrangement,
  defaultGain: 0.08
}
const TRAP_BASS_STEM_TYPE: StemGenerator = {
  type: StemGeneratorType.BASS,
  categories: ['Synth Bass'],
  generateArrangement: TrapSongGenerator.generateBassArrangement,
  defaultGain: 0.3
}
